import React, { useEffect, useRef, useMemo, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Button, Row, Spinner, Table, TextField, Typography } from "@coolremote/shared";
import { useCreateModbusDeviceTemplate, useGetSiteModbusDeviceTemplates } from "../../../../hooks/useModbusDeviceTemplatesApi";
import { IModbusDeviceTemplate } from "../../types";
import styles from "./ImportDeviceTemplateDialog.module.css";

interface IProps {
  open: boolean;
  siteId?: string;
  onClose: () => void;
  onLoad: (template: IModbusDeviceTemplate) => void;
  onImportSuccess?: () => void;
}

const ImportDeviceTemplateDialog: React.FC<IProps> = ({ open, siteId, onClose, onLoad, onImportSuccess }) => {
  const [search, setSearch] = useState("");
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: createDeviceTemplate } = useCreateModbusDeviceTemplate({});
  // Self-load the library list; reload it fresh each time the modal opens.
  const { data, isLoading, refetch } = useGetSiteModbusDeviceTemplates(siteId, open) as unknown as {
    data: IModbusDeviceTemplate[] | null;
    isLoading: boolean;
    refetch: () => void;
  };
  const templates = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    if (open) refetch();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.vendorName?.toLowerCase().includes(q) ||
        t.modelName?.toLowerCase().includes(q),
    );
  }, [templates, search]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !siteId) return;
    e.target.value = "";

    const text = await file.text();
    let items: IModbusDeviceTemplate[];
    try {
      const parsed = JSON.parse(text);
      items = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return;
    }

    setImportProgress({ done: 0, total: items.length });
    for (let i = 0; i < items.length; i++) {
      try {
        await createDeviceTemplate({ ...items[i], site: siteId });
      } catch {}
      setImportProgress({ done: i + 1, total: items.length });
    }
    setImportProgress(null);
    refetch();
    onImportSuccess?.();
  };

  const importing = importProgress !== null;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t`Device Template Library`}
      hideConfirm
      cancelLabel={t`Close`}
      cancelSize="medium"
      size="large"
    >
      <div className={styles.searchWrap}>
        <div className={styles.searchField}>
          <TextField
            fullWidth
            placeholder={t`Search templates…`}
            value={search}
            onChange={(v: string) => setSearch(v)}
          />
        </div>
        <Row gap={8} align="center">
          {siteId && (
            importing ? (
              <div className={styles.importProgress}>
                <Spinner size={14} />
                <Typography variant="meta">
                  {t`Importing`} {importProgress.done} / {importProgress.total}…
                </Typography>
              </div>
            ) : (
              <>
                <Button white size="medium" onClick={() => fileInputRef.current?.click()}>
                  {t`Import`}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </>
            )
          )}
        </Row>
      </div>

      {isLoading ? (
        <div className={styles.loaderWrap}>
          <Spinner size={28} />
        </div>
      ) : (
      <Table
        data={filtered}
        getRowKey={(row: IModbusDeviceTemplate, index: number) => row.id ?? index}
        emptyMessage={search ? t`No templates match your search.` : t`No device templates found for this site.`}
        columns={[
          {
            id: "name",
            title: t`DEVICE NAME`,
            width: "25%",
            render: (row: IModbusDeviceTemplate) => (
              <>
                {row.name}
              </>
            ),
          },
          {
            id: "units",
            title: t`UNIT COUNT`,
            width: "15%",
            align: "center",
            render: (row: IModbusDeviceTemplate) => (row.units?.length ?? 0),
          },
          {
            id: "registers",
            title: t`REGISTER COUNT`,
            width: "15%",
            align: "center",
            render: (row: IModbusDeviceTemplate) =>
              (row.units ?? []).reduce((sum, u) => sum + (u.rows?.length ?? 0), 0),
          },
          {
            id: "unitNames",
            title: t`FUNCTIONAL UNITS`,
            width: "25%",
            render: (row: IModbusDeviceTemplate) => (
              <div className={styles.paramList}>
                {(row.units ?? []).map((u, i) => (u.name)).join(', ')}
              </div>
            ),
          },
          {
            id: "action",
            width: "20%",
            align: "right" as const,
            render: (row: IModbusDeviceTemplate) => (
              <Button white size="small" onClick={() => onLoad(row)}>
                {t`Select`}
              </Button>
            ),
          },
        ]}
      />
      )}
    </BaseDialog>
  );
};

export default ImportDeviceTemplateDialog;
